import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";
import { COMPANY } from "@/lib/company";
import { HeartHandshake, CalendarCheck, ShieldCheck, Users, ArrowRight, MessageCircle } from "lucide-react";

const STEPS = [
  { icon: Users, title: "Tu nous décris ta demande", text: "Situation, type de permis, délai souhaité : un formulaire de 2 minutes, sans engagement." },
  { icon: HeartHandshake, title: "On qualifie ta demande", text: "On étudie ta demande par email pour bien comprendre ton besoin, avant toute mise en relation." },
  { icon: CalendarCheck, title: "On t'oriente vers le bon partenaire", text: "Une fois ta demande qualifiée, tu reçois par email les coordonnées de l'auto-école de notre réseau la plus adaptée : c'est toi qui la contactes, sous 2 jours." },
  { icon: ShieldCheck, title: "Tu avances sereinement", text: "Tu choisis librement ton auto-école et tu règles ta formation directement auprès d'elle." },
];

const VALUES = [
  { title: "PlanPermis n'est pas une auto-école", text: "Nous sommes un service de mise en relation. Nous ne dispensons aucune formation : nous t'orientons vers un réseau d'auto-écoles agréées." },
  { title: "Un partenaire principal d'exception", text: "Notre partenaire principal cumule plus de 130 avis 5★ et fait réussir ses élèves en ~1,5 passage en moyenne (cursus classique). Les autres auto-écoles du réseau prennent le relais selon les besoins." },
  { title: "Une qualification humaine", text: "On qualifie chaque demande par email pour bien comprendre le besoin, avant toute mise en relation. Tu n'es jamais un simple numéro." },
];

export default function QuiSommesNous() {
  useSeo({
    title: "Qui sommes-nous ? | PlanPermis",
    description:
      "PlanPermis te permet de réserver en ligne ta date d'examen du permis auprès d'auto-écoles partenaires.",
  });
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="page-qui-sommes-nous">
      <header className="border-b border-neutral-200 bg-white px-6 py-4 sm:px-12 lg:px-20">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" data-testid="about-home-link">
            <Logo className="h-10" />
          </Link>
          <div className="flex items-center gap-fib-4 text-gr-sm font-semibold">
            <Link to="/blog" data-testid="about-blog-link" className="hidden text-neutral-600 hover:text-brand sm:inline">Blog</Link>
            <Link to="/ambassadeur" data-testid="about-ambassador-link" className="hidden text-neutral-600 hover:text-brand sm:inline">Devenir ambassadeur</Link>
            <Link
              to="/"
              data-testid="about-cta-top"
              className="rounded-full bg-brand px-5 py-2 text-white hover:opacity-90"
            >
              Demander une date
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero / mission */}
        <section className="px-6 py-16 sm:px-12 lg:px-20">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Qui sommes-nous</p>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-neutral-900 sm:text-5xl" data-testid="about-title">
              On t&apos;aide à passer ton permis, <span className="text-brand">sans galérer</span>.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-neutral-600">
              {COMPANY.brand} est un <strong>service de mise en relation</strong> — nous ne sommes pas une
              auto-école. Notre mission : t&apos;éviter les mois d&apos;attente et le stress. On qualifie
              d&apos;abord ta demande, puis tu reçois par email les coordonnées de l&apos;auto-école
              de notre <strong>réseau de partenaires</strong> la plus adaptée à ta situation — à commencer par
              notre partenaire principal, avec plus de <strong>130 avis 5★</strong> et une réussite en{" "}
              <strong>~1,5 passage</strong> en moyenne (cursus classique).
            </p>
          </div>
        </section>

        {/* Notre histoire */}
        <section className="px-6 pb-4 sm:px-12 lg:px-20">
          <div className="mx-auto max-w-3xl rounded-2xl border border-neutral-200 bg-white p-8">
            <h2 className="text-2xl font-extrabold tracking-tight text-neutral-900">Pourquoi PlanPermis ?</h2>
            <p className="mt-4 leading-relaxed text-neutral-600">
              Trop d&apos;élèves attendent des mois une place d&apos;examen, ou enchaînent les échecs faute
              d&apos;un accompagnement adapté. On a créé {COMPANY.brand} pour changer ça : qualifier chaque
              demande par téléphone, puis orienter chaque élève vers l&apos;auto-école de notre réseau la plus
              adaptée. {COMPANY.brand} reste un intermédiaire indépendant : c&apos;est toi qui choisis et règles
              ta formation auprès de l&apos;auto-école. Simple et humain.
            </p>
          </div>
        </section>

        {/* Comment ça marche */}
        <section className="px-6 py-14 sm:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-3xl font-extrabold tracking-tight text-neutral-900">Comment ça marche</h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <div key={s.title} className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid={`about-step-${i + 1}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <p className="mt-4 text-sm font-bold text-neutral-900">{i + 1}. {s.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-500">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nos engagements */}
        <section className="px-6 pb-16 sm:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-3xl font-extrabold tracking-tight text-neutral-900">Nos engagements</h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-3">
              {VALUES.map((v, i) => (
                <div key={v.title} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-neutral-100" data-testid={`about-value-${i + 1}`}>
                  <p className="text-base font-bold text-neutral-900">{v.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-500">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 pb-20 sm:px-12 lg:px-20">
          <div className="mx-auto max-w-4xl rounded-3xl bg-neutral-900 px-8 py-12 text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-white">Prêt à trouver ta date d&apos;examen ?</h2>
            <p className="mt-3 text-neutral-300">C&apos;est rapide et sans engagement.</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/"
                data-testid="about-cta-form"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3 text-sm font-semibold text-white hover:opacity-90"
              >
                Faire ma demande <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`https://wa.me/${COMPANY.phone.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3 text-sm font-semibold text-white hover:bg-white/10"
              >
                <MessageCircle className="h-4 w-4" /> Nous contacter
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
